import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import {
  LayoutGrid,
  LineChart as LineChartIcon,
  Cpu,
  Siren,
  ChevronRight,
  Router,
  Activity,
  Thermometer,
  Gauge,
  Signal,
  Clock,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine
} from 'recharts';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Mock data for graphs - simplified and made more dynamic
const generateFleetData = () => Array.from({ length: 7 }, (_, i) => {
  const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i];
  return {
    name: day,
    distance: Math.floor(80 + Math.random() * 120),
    vehicles: Math.floor(180 + Math.random() * 50)
  };
});

const generateIoTData = () => Array.from({ length: 12 }, (_, i) => {
  const hour = i * 2;
  return {
    time: `${String(hour).padStart(2, '0')}:00`,
    temp: Math.round((35 + Math.sin(i * 0.5) * 15 + Math.random() * 5) * 10) / 10,
    humidity: Math.round((55 + Math.cos(i * 0.3) * 15 + Math.random() * 5) * 10) / 10
  };
});

const generateGatewayData = () => Array.from({ length: 20 }, (_, i) => ({
  time: `${String(Math.floor(i * 1.2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`,
  temperature: Math.round((42 + Math.sin(i * 0.4) * 18 + Math.random() * 6) * 10) / 10,
  pressure: Math.round((1013 + Math.sin(i * 0.3) * 15 + Math.random() * 5) * 10) / 10
}));

const edgeCpuData = [{ name: 'Load', value: 67 }, { name: 'Free', value: 33 }];
const edgeStorageData = [{ name: 'Used', value: 75 }, { name: 'Free', value: 25 }];
const alertCategories = [
  { name: 'Critical', value: 2, color: '#ef4444' },
  { name: 'Warning', value: 5, color: '#f59e0b' },
  { name: 'Info', value: 10, color: '#3b82f6' },
];
const CHART_COLORS = {
  primary: '#3b82f6',
  secondary: '#10b981',
  accent: '#8b5cf6',
  warning: '#f59e0b',
  danger: '#ef4444',
  muted: '#94a3b8',
  border: '#e2e8f0',
  tooltipBg: '#ffffff',
  tooltipFg: '#1e293b'
};

export default function AnalyticsDashboardPreview({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  const [activeTab, setActiveTab] = useState('gateway');
  const [fleetData, setFleetData] = useState(generateFleetData());
  const [iotData, setIotData] = useState(generateIoTData());
  const [gatewayData, setGatewayData] = useState(generateGatewayData());

  useEffect(() => {
    const interval = setInterval(() => {
      setFleetData(generateFleetData());
      setIotData(generateIoTData());
      setGatewayData(prev => {
        const newPoint = {
          time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
          temperature: Math.round((42 + Math.sin(Date.now() / 3000) * 18 + Math.random() * 6) * 10) / 10,
          pressure: Math.round((1013 + Math.sin(Date.now() / 5000) * 15 + Math.random() * 5) * 10) / 10
        };
        return [...prev.slice(1), newPoint];
      });
    }, 3000); // Refresh data every 3 seconds for demo
    return () => clearInterval(interval);
  }, []);

  const dashboardTabs = [
    { id: 'gateway', name: t('homepage.dashboard.tabs.gateway', 'IoT Gateway'), icon: Router },
    { id: 'fleet', name: t('homepage.dashboard.tabs.fleet', 'Fleet Analytics'), icon: LayoutGrid },
    { id: 'edge', name: t('homepage.dashboard.tabs.edge', 'Edge Devices'), icon: Cpu },
    { id: 'alerts', name: t('homepage.dashboard.tabs.alerts', 'System Alerts'), icon: Siren }
  ];


  const renderDashboardContent = (tabId) => {
    switch (tabId) {
      case 'gateway':
        const currentTemp = gatewayData[gatewayData.length - 1]?.temperature || 0;
        const currentPressure = gatewayData[gatewayData.length - 1]?.pressure || 0;
        return (
          <div className="space-y-4">
            {/* Gateway Status Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <Router className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-900">AETI Gateway IGW-4G</div>
                  <div className="text-xs text-gray-500">Serial: IGW-4G-2024-0892</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Signal className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-600">-62 dBm</span>
                </div>
                <Badge className="bg-emerald-500 text-white text-[10px]">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />
                  LIVE
                </Badge>
              </div>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <Thermometer className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-gray-500">Temperature</span>
                </div>
                <div className="text-xl font-bold text-red-600">{currentTemp}°C</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <Gauge className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500">Pressure</span>
                </div>
                <div className="text-xl font-bold text-blue-600">{currentPressure} hPa</div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500">Uptime</span>
                </div>
                <div className="text-xl font-bold text-emerald-600">99.9%</div>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-gray-500">Data Rate</span>
                </div>
                <div className="text-xl font-bold text-amber-600">12.4k/s</div>
              </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={isMobile ? 180 : 200}>
              <AreaChart data={gatewayData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.danger} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.danger} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.border} />
                <XAxis dataKey="time" fontSize={10} stroke={CHART_COLORS.muted} />
                <YAxis fontSize={10} stroke={CHART_COLORS.muted} />
                <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <ReferenceLine y={75} stroke={CHART_COLORS.danger} strokeDasharray="5 5" />
                <Area type="monotone" dataKey="temperature" stroke={CHART_COLORS.danger} strokeWidth={2} fill="url(#tempGrad)" name="Temperature (°C)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      case 'fleet':
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
            <BarChart data={fleetData} margin={{ top: 5, right: isMobile ? 0 : 10, left: isMobile ? -30 : -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.border} />
              <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <YAxis fontSize={11} tickLine={false} axisLine={{stroke: CHART_COLORS.border}} stroke={CHART_COLORS.muted} />
              <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
              <Legend verticalAlign="top" height={36} iconSize={10} wrapperStyle={{fontSize: "0.8rem"}}/>
              <Bar dataKey="distance" fill={CHART_COLORS.primary} radius={[3, 3, 0, 0]} barSize={isMobile ? 10 : 12} name={t('common.distance', 'Distance (km)')}/>
              <Bar dataKey="vehicles" fill={CHART_COLORS.secondary} radius={[3, 3, 0, 0]} barSize={isMobile ? 10 : 12} name={t('common.vehicles', 'Active Vehicles')}/>
            </BarChart>
          </ResponsiveContainer>
        );
      case 'edge':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 items-center h-full">
            {[ { titleKey: 'cpu', data: edgeCpuData, color: CHART_COLORS.primary }, 
               { titleKey: 'storage', data: edgeStorageData, color: CHART_COLORS.secondary } ].map(chart => (
              <div key={chart.titleKey} className="flex flex-col items-center">
                <p className="text-xs text-muted-foreground mb-1">{t(`homepage.dashboard.edge.${chart.titleKey}`, chart.titleKey)}</p>
                <ResponsiveContainer width="100%" height={isMobile ? 120 : 140}>
                  <PieChart>
                    <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" 
                         outerRadius={isMobile ? 45:55} innerRadius={isMobile? 25:30} 
                         labelLine={false} 
                         label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} 
                         fontSize={isMobile? 9:10} fontWeight={500}
                    >
                      <Cell fill={chart.color} />
                      <Cell fill={CHART_COLORS.border} />
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        );
      case 'alerts':
        return (
          <div className="space-y-2.5 md:space-y-3 py-2">
            {alertCategories.map((alert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.08, duration: 0.3 }}
                className="flex items-center justify-between p-3 rounded-lg border"
                style={{ 
                  backgroundColor: `${alert.color}10`,
                  borderColor: `${alert.color}30`
                }}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" style={{ color: alert.color }} />
                  <span className="font-medium text-xs sm:text-sm" style={{color: alert.color}}>{alert.name}</span>
                </div>
                <Badge variant="outline" style={{ borderColor: alert.color, color: alert.color }}>
                  {alert.value}
                </Badge>
              </motion.div>
            ))}
          </div>
        );
      default: return null;
    }
  };

  return (
    <section
      id="dashboard-preview"
      className="relative py-20 md:py-28 lg:py-32 bg-muted/30 overflow-hidden"
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.dashboard.badge', 'Data-Driven Decisions')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.dashboard.title', 'Visualize Your Success')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.dashboard.subtitle', 'Explore interactive dashboards that provide deep insights into your fleet, IoT devices, edge performance, and critical alerts.')}
            </p>
          </motion.div>
        </motion.div>

        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
          <Card className="w-full max-w-4xl mx-auto shadow-xl shadow-primary/5 border-border/20 bg-background/80 backdrop-blur-lg rounded-xl overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} dir={isRTL ? 'rtl' : 'ltr'} className="w-full">
              <CardHeader className="p-0 border-b border-border/50">
                <TabsList className={cn(
                  "grid w-full rounded-none h-auto p-1",
                  `grid-cols-${dashboardTabs.length}`,
                  isMobile && "grid-cols-2"
                )}>
                  {dashboardTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className={cn(
                          "items-center justify-center gap-1.5 sm:gap-2 px-2 py-2.5 sm:px-3 sm:py-3 h-auto text-xs sm:text-sm font-medium rounded-none transition-all",
                          "data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-[inset_0_-2px_0_0_hsl(var(--primary))]",
                          "hover:bg-muted/50 data-[state=inactive]:text-muted-foreground",
                          "flex flex-row"
                        )}
                      >
                        <Icon size={isMobile ? 16 : 18} />
                        <span>{tab.name}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-5 min-h-[300px] sm:min-h-[350px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="w-full h-full"
                  >
                    {renderDashboardContent(activeTab)}
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Tabs>
             <CardDescription className="p-3 sm:p-4 text-center text-xs text-muted-foreground border-t border-border/50">
                {t('homepage.dashboard.demoNotice', 'Live dashboard preview. Data shown is for demonstration purposes.')}
            </CardDescription>
          </Card>
        </motion.div>

        <motion.div 
            variants={fadeInUp} initial="hidden" 
            whileInView="visible" viewport={{ once: true, amount: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 md:mt-12"
        >
            <Button size="lg" asChild className="group">
                <a href="/demo">
                    {t('homepage.dashboard.ctaDemo', 'Try Interactive Demo')}
                    <ChevronRight size={20} className={cn("ml-2 transition-transform group-hover:translate-x-1", isRTL && "ml-0 mr-2 group-hover:-translate-x-1")} />
                </a>
            </Button>
            <Button size="lg" variant="outline" asChild className="group">
                <a href="/dashboard">
                    {t('homepage.dashboard.cta', 'Explore Full Dashboard')}
                    <ChevronRight size={20} className={cn("ml-2 transition-transform group-hover:translate-x-1", isRTL && "ml-0 mr-2 group-hover:-translate-x-1")} />
                </a>
            </Button>
        </motion.div>

      </div>
    </section>
  );
}
